import { z } from 'zod';
import { HelpAnchorId } from './ai.js';
import { AwardTypeCode } from './awards.js';
import { RoleKey } from './me.js';
import { PageInfo, pageFields } from './paging.js';

export const HELP_PAGE_KEYS = [
  'home',
  'my-rewards',
  'my-award',
  'election',
  'letters-to-hand-out',
  'recognize',
  'award-entry',
  'spot',
  // APP-222: Spot Award setup (Settings › Award setup).
  'spot-setup',
  'my-awards',
  'award-record',
  'approvals',
  'work-queue',
  'work-check',
  'work-letters',
  'work-past-deadline',
  'work-shareworks',
  'work-earning-statements',
  'payroll',
  'insights',
  'insights-ask',
  'executive',
  'budget-pools',
  'values',
  'sector-settings',
  'executive-delegates',
  'charge-codes',
  'elections-letters',
  'payroll-settings',
  'help-catalog',
  'messages',
  'my-settings',
  'my-feedback',
  'training',
  'admin',
  'admin-ai',
  'admin-feedback',
] as const;
export const HelpCatalogPageKey = z.enum(HELP_PAGE_KEYS);
export type HelpCatalogPageKey = z.infer<typeof HelpCatalogPageKey>;

const text = (max: number) =>
  z
    .string()
    .max(max)
    .refine((v) => !v.includes('\0'), 'Null characters are not supported');
const ArticleId = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const HelpCatalogAudience = z.union([
  z.literal('everyone'),
  z.array(RoleKey).min(1).max(RoleKey.options.length),
]);
const Actor = z.object({ id: z.uuid(), name: text(200) });

/** A complete plain-text document; publication applies stricter editorial/reference checks. */
export const HelpArticleContent = z
  .object({
    title: text(160).refine((v) => v.trim().length > 0, 'Enter a title'),
    summary: text(500),
    category: text(80),
    body: z.array(text(4000)).max(32),
    steps: z.array(text(2000)).max(32).default([]),
    keywords: text(2000).default(''),
    related: z.array(ArticleId).max(50).default([]),
    start: z
      .object({
        label: text(120),
        path: z
          .string()
          .max(160)
          .regex(/^\/(?:[a-z0-9-]+(?:\/[a-z0-9-]+)*)?$/),
      })
      .strict()
      .nullable()
      .default(null),
    show: z.array(HelpAnchorId).max(HelpAnchorId.options.length).default([]),
    audience: HelpCatalogAudience,
    /**
     * Also for anyone who can start one of these award types (APP-090 §8, 2026-10-21): the
     * Recognize audience a System Admin sets in Award types & features decides who that is, so
     * someone named to start an award reads its how-to without holding a role.
     */
    starters: z
      .array(AwardTypeCode)
      .max(AwardTypeCode.options.length)
      .refine((v) => new Set(v).size === v.length, 'Choose each award type once')
      .default([]),
    pages: z.array(HelpCatalogPageKey).max(HELP_PAGE_KEYS.length),
    popular: z.boolean().default(false),
  })
  .strict()
  .refine(
    (v) => new TextEncoder().encode(JSON.stringify(v)).length <= 65_536,
    'Article is too large',
  );
export type HelpArticleContent = z.infer<typeof HelpArticleContent>;

export const HelpCatalogStatus = z.enum(['draft', 'published', 'changes', 'archived']);
export type HelpCatalogStatus = z.infer<typeof HelpCatalogStatus>;
export const HelpCatalogQuery = z
  .object({
    q: z.string().trim().max(200).default(''),
    status: z.enum(['all', 'active', ...HelpCatalogStatus.options]).default('all'),
    category: text(80).optional(),
    audience: z.enum(['everyone', ...RoleKey.options]).optional(),
    /** The Help topic an article is shown on. Named `screen` so `page` can be the list's page. */
    screen: HelpCatalogPageKey.optional(),
    content: z.enum(['both', 'draft', 'published']).default('both'),
    ...pageFields,
  })
  .strict();
export type HelpCatalogQuery = z.infer<typeof HelpCatalogQuery>;
const Updated = { updatedBy: Actor.nullable(), updatedAt: z.iso.datetime() };
export const HelpCatalogDetail = z.object({
  id: ArticleId,
  version: z.number().int().positive(),
  draft: HelpArticleContent.nullable(),
  published: HelpArticleContent.nullable(),
  archived: z.boolean(),
  ...Updated,
});
export type HelpCatalogDetail = z.infer<typeof HelpCatalogDetail>;
const Facet = z.object({ value: z.string(), count: z.number().int().nonnegative() });
export const HelpCatalogList = z.object({
  items: z.array(
    z.object({
      id: ArticleId,
      version: z.number().int().positive(),
      status: HelpCatalogStatus,
      title: z.string(),
      summary: z.string(),
      category: z.string(),
      audience: HelpCatalogAudience,
      starters: z.array(AwardTypeCode).default([]),
      pages: z.array(HelpCatalogPageKey),
      ...Updated,
      match: z
        .object({
          source: z.enum(['draft', 'published']),
          text: z.string(),
          highlights: z.array(
            z.object({
              start: z.number().int().nonnegative(),
              end: z.number().int().nonnegative(),
            }),
          ),
        })
        .nullable(),
    }),
  ),
  total: z.number().int().nonnegative(),
  page: PageInfo,
  facets: z.object({
    categories: z.array(Facet),
    audiences: z.array(Facet),
    pages: z.array(Facet),
    statuses: z.array(Facet),
  }),
  query: HelpCatalogQuery,
});
export type HelpCatalogList = z.infer<typeof HelpCatalogList>;
export const HelpCatalogAction = z.enum([
  'baseline',
  'save',
  'publish',
  'archive',
  'discard',
  'restore',
]);
export const HelpCatalogRevision = z.object({
  id: z.uuid(),
  articleId: ArticleId,
  version: z.number().int().positive(),
  action: HelpCatalogAction,
  content: HelpArticleContent.nullable(),
  actor: Actor.nullable(),
  at: z.iso.datetime(),
  reason: text(1000).nullable(),
});
export type HelpCatalogRevision = z.infer<typeof HelpCatalogRevision>;
export const HelpDraftSave = z
  .object({ version: z.number().int().nonnegative().default(0), content: HelpArticleContent })
  .strict();
export type HelpDraftSave = z.infer<typeof HelpDraftSave>;
export const HelpCatalogCommand = z
  .object({ version: z.number().int().positive(), reason: text(1000).trim().default('') })
  .strict();
export type HelpCatalogCommand = z.infer<typeof HelpCatalogCommand>;
export const HelpCatalogRestore = HelpCatalogCommand.extend({ revisionId: z.uuid().optional() });
export type HelpCatalogRestore = z.infer<typeof HelpCatalogRestore>;
export const HelpCatalogMetadata = z.object({
  pages: z.array(z.object({ key: HelpCatalogPageKey, label: z.string() })),
  roles: z.array(z.object({ key: RoleKey, label: z.string() })),
  anchors: z.array(
    z.object({
      id: HelpAnchorId,
      page: HelpCatalogPageKey,
      label: z.string(),
      audience: HelpCatalogAudience,
    }),
  ),
  starts: z.array(
    z.object({
      path: z.string(),
      label: z.string(),
      audience: HelpCatalogAudience,
      /** The award types whose starters may open it (an award's entry form). */
      starters: z.array(AwardTypeCode).default([]),
    }),
  ),
  /** The award types an article can also be for, by their names. */
  awardTypes: z.array(z.object({ key: AwardTypeCode, label: z.string() })).default([]),
});
export type HelpCatalogMetadata = z.infer<typeof HelpCatalogMetadata>;
